import React, { useState, useEffect } from 'react';
import { adminAPI, clientsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Users, Pencil, Loader2, Plus, UserPlus, Power, Eye } from 'lucide-react';
import { formatDate, getRoleLabel } from '../../lib/utils';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  
  // New employee form state
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    telephone: '',
    poste: '',
    date_embauche: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        adminAPI.getUsers(),
        clientsAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setSelectedRole(user.role);
    setSelectedClient(user.client_id || '');
    setDialogOpen(true);
  };

  const handleViewUser = async (user) => {
    try {
      const res = await adminAPI.getUser(user.id);
      setViewUser(res.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedRole !== editingUser.role) {
        await adminAPI.updateUserRole(editingUser.id, selectedRole);
      }
      if (selectedRole === 'client' && selectedClient && selectedClient !== editingUser.client_id) {
        await adminAPI.assignUserToClient(editingUser.id, selectedClient);
      }
      toast.success('Utilisateur mis à jour');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await adminAPI.toggleUserActive(user.id);
      toast.success(user.actif !== false ? 'Utilisateur désactivé' : 'Utilisateur activé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleCreateEmployee = async () => {
    if (!newEmployee.email || !newEmployee.password || !newEmployee.nom || !newEmployee.prenom) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.createEmployee(newEmployee);
      toast.success('Employé créé avec succès');
      setCreateDialogOpen(false);
      setNewEmployee({
        email: '',
        password: '',
        nom: '',
        prenom: '',
        telephone: '',
        poste: '',
        date_embauche: new Date().toISOString().split('T')[0],
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'administrateur':
        return 'bg-purple-100 text-purple-800';
      case 'employe':
        return 'bg-blue-100 text-blue-800';
      case 'client':
        return 'bg-green-100 text-green-800';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Utilisateurs</h1>
          <p className="text-slate-600">Gérez les utilisateurs et leurs rôles.</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-employee-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Nouvel employé
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucun utilisateur.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Courriel</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="hidden md:table-cell">Poste</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className={user.actif === false ? 'opacity-50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-primary font-medium text-sm">
                            {user.prenom?.[0]}{user.nom?.[0]}
                          </span>
                        </div>
                        <span className="font-medium">{user.prenom} {user.nom}</span>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)} variant="secondary">
                        {getRoleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.actif !== false ? 'default' : 'secondary'} 
                             className={user.actif !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {user.actif !== false ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {user.poste || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewUser(user)}
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          data-testid={`edit-user-${user.id}`}
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(user)}
                          title={user.actif !== false ? 'Désactiver' : 'Activer'}
                          className={user.actif !== false ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier utilisateur</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Utilisateur</p>
                <p className="font-medium">{editingUser.prenom} {editingUser.nom}</p>
                <p className="text-sm text-slate-600">{editingUser.email}</p>
              </div>

              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administrateur">Administrateur</SelectItem>
                    <SelectItem value="employe">Employé</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedRole === 'client' && (
                <div className="space-y-2">
                  <Label>Associer à un client</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger data-testid="user-client-select">
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.nom_entreprise}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="user-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Employee Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un employé</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={newEmployee.prenom}
                  onChange={(e) => setNewEmployee({ ...newEmployee, prenom: e.target.value })}
                  placeholder="Jean"
                  data-testid="new-employee-prenom"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newEmployee.nom}
                  onChange={(e) => setNewEmployee({ ...newEmployee, nom: e.target.value })}
                  placeholder="Dupont"
                  data-testid="new-employee-nom"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Courriel *</Label>
              <Input
                type="email"
                value={newEmployee.email}
                onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                placeholder="jean.dupont@peyconsultants.ca"
                data-testid="new-employee-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Mot de passe *</Label>
              <Input
                type="password"
                value={newEmployee.password}
                onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                placeholder="••••••••"
                data-testid="new-employee-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                value={newEmployee.telephone}
                onChange={(e) => setNewEmployee({ ...newEmployee, telephone: e.target.value })}
                placeholder="514-555-1234"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Poste / Titre</Label>
              <Input
                value={newEmployee.poste}
                onChange={(e) => setNewEmployee({ ...newEmployee, poste: e.target.value })}
                placeholder="Ingénieur civil"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Date embauche</Label>
              <Input
                type="date"
                value={newEmployee.date_embauche}
                onChange={(e) => setNewEmployee({ ...newEmployee, date_embauche: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateEmployee} disabled={saving} data-testid="create-employee-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Créer employé
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profil utilisateur</DialogTitle>
          </DialogHeader>
          {viewUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-xl">
                    {viewUser.prenom?.[0]}{viewUser.nom?.[0]}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{viewUser.prenom} {viewUser.nom}</h3>
                  <p className="text-slate-600">{viewUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-slate-500">Rôle</p>
                  <p className="font-medium">{getRoleLabel(viewUser.role)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Statut</p>
                  <p className="font-medium">{viewUser.actif !== false ? 'Actif' : 'Inactif'}</p>
                </div>
                {viewUser.poste && (
                  <div>
                    <p className="text-sm text-slate-500">Poste</p>
                    <p className="font-medium">{viewUser.poste}</p>
                  </div>
                )}
                {viewUser.telephone && (
                  <div>
                    <p className="text-sm text-slate-500">Téléphone</p>
                    <p className="font-medium">{viewUser.telephone}</p>
                  </div>
                )}
                {viewUser.date_embauche && (
                  <div>
                    <p className="text-sm text-slate-500">Date embauche</p>
                    <p className="font-medium">{formatDate(viewUser.date_embauche)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Inscrit le</p>
                  <p className="font-medium">{formatDate(viewUser.created_at)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
